import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { GHOST_BTN } from './ui';
import './listPage.css';

/**
 * Κάτω από τον πίνακα (DataTable `footer`). Pagination: σελίδες από τον server («1–50 από 230», ‹ Σελίδα
 * 1 από 5 ›)· δεν φαίνεται με μία σελίδα. LoadMore: «Περισσότερα» για λίστες που μεγαλώνουν προς τα κάτω.
 */

const PAGER_BTN = 'inline-flex items-center justify-center w-11 h-11 shrink-0 rounded-xl border border-line-strong bg-surface-2 '
  + 'text-t1 hover:bg-surface-3 transition disabled:opacity-40 disabled:cursor-not-allowed';

export interface PaginationProps {
  /** 1 = πρώτη. */
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  /** Όσο φορτώνει: τα κουμπιά δεν πατιούνται. */
  disabled?: boolean;
  testId?: string;
}

export const Pagination: React.FC<PaginationProps> = ({ page, pageSize, total, onPage, disabled = false, testId = 'pagination' }) => {
  const { t } = useTranslation(['listPage']);
  const size = Math.max(1, pageSize);
  const pages = Math.max(1, Math.ceil(Math.max(0, total) / size));
  if (pages <= 1) return null;
  const current = Math.min(Math.max(1, page), pages);
  const from = (current - 1) * size + 1;
  const to = Math.min(total, current * size);
  return (
    <nav className="lp-pager" aria-label={t('listPage:pagination.label')} data-testid={testId}>
      <span className="lp-pager-range">{t('listPage:pagination.range', { from, to, total })}</span>
      <span className="lp-pager-nav">
        <button type="button" className={PAGER_BTN} onClick={() => onPage(current - 1)} disabled={disabled || current <= 1}
          aria-label={t('listPage:pagination.previous')} title={t('listPage:pagination.previous')}>
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        </button>
        <span className="lp-pager-page" aria-current="page">{t('listPage:pagination.page', { page: current, pages })}</span>
        <button type="button" className={PAGER_BTN} onClick={() => onPage(current + 1)} disabled={disabled || current >= pages}
          aria-label={t('listPage:pagination.next')} title={t('listPage:pagination.next')}>
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </span>
    </nav>
  );
};

export interface LoadMoreProps {
  /** Πόσα φαίνονται ήδη. */
  shown: number;
  /** Όλα όσα υπάρχουν, αν τα ξέρει ο server· χωρίς αυτό το κουμπί μένει ώσπου η σελίδα να σταματήσει να το δείχνει. */
  total?: number;
  loading?: boolean;
  onLoadMore: () => void;
  /** Αντί για «Περισσότερα». */
  label?: string;
  testId?: string;
}

export const LoadMore: React.FC<LoadMoreProps> = ({ shown, total, loading = false, onLoadMore, label, testId = 'load-more' }) => {
  const { t } = useTranslation(['listPage']);
  if (total !== undefined && shown >= total) return null;
  return (
    <div className="lp-more" data-testid={testId}>
      {total !== undefined && <span className="lp-more-count">{t('listPage:loadMore.shown', { shown, total })}</span>}
      <button type="button" className={GHOST_BTN} onClick={onLoadMore} disabled={loading} aria-busy={loading || undefined}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        {label || t('listPage:loadMore.button')}
      </button>
    </div>
  );
};

export default Pagination;
